import { 
  FrameIcon, 
  Share2Icon, 
  CodeIcon, 
  TagIcon, 
  BookOpenIcon,
  VideoIcon,
  DownloadIcon,
  SettingsIcon,
  UsersIcon,
  DatabaseIcon
} from 'lucide-react';

const DocumentationLanding = () => {
  const documentationSections = [
    {
      title: "Creating Monet Frames",
      description: "Learn how to transform your 3D scans into interactive Monet Frames",
      icon: FrameIcon,
      link: "/docs/frames",
      subsections: [
        "From 3D scans to interactive frames",
        "Frame customization options",
        "Embedding frames in websites"
      ]
    },
    {
      title: "Sharing Digital Twins",
      description: "Guide to sharing and collaborating on digital twins",
      icon: Share2Icon,
      link: "/docs/sharing",
      subsections: [
        "Generating shareable links",
        "Permission management",
        "Collaboration features"
      ]
    },
    {
      title: "API Integration",
      description: "Connect your digital twins to external systems",
      icon: CodeIcon,
      link: "/docs/api",
      subsections: [
        "Authentication setup",
        "Real-time data streaming",
        "Event triggers and webhooks"
      ]
    },
    {
      title: "3D Model Preparation",
      description: "Prepare your models for Monet Digital Twins",
      icon: TagIcon,
      link: "/docs/preparation",
      subsections: [
        "Node labeling conventions",
        "Material optimization",
        "Texture requirements"
      ]
    },
    {
      title: "Getting Started Guide",
      description: "Beginner's guide to the Monet platform",
      icon: BookOpenIcon,
      link: "/docs/getting-started",
      subsections: [
        "Account setup",
        "First digital twin creation",
        "Basic navigation"
      ]
    },
    {
      title: "Video Tutorials",
      description: "Step-by-step video guides",
      icon: VideoIcon,
      link: "/docs/videos",
      subsections: [
        "Frame creation walkthrough",
        "API integration demo",
        "Advanced features"
      ]
    }
  ];

  const quickLinks = [
    {
      title: "Download SDK",
      icon: DownloadIcon,
      link: "/downloads/sdk"
    },
    {
      title: "API Reference",
      icon: SettingsIcon,
      link: "/api-reference"
    },
    {
      title: "Community Forum",
      icon: UsersIcon,
      link: "https://community.monet.io"
    },
    {
      title: "Sample Models",
      icon: DatabaseIcon,
      link: "/samples"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-gray-100">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Monet Platform Documentation
          </h1>
          <p className="text-xl text-gray-600">
            Comprehensive guides to create, share, and integrate digital twins
          </p>
        </div>

        {/* Main Documentation Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {documentationSections.map((section, index) => (
            <a 
              key={index} 
              href={section.link}
              className="group bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="bg-monet-blue/10 p-3 rounded-lg">
                    <section.icon className="text-monet-blue w-6 h-6" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 group-hover:text-monet-blue transition-colors">
                    {section.title}
                  </h2>
                </div>
                <p className="text-gray-600 mb-4">{section.description}</p>
                <ul className="space-y-2">
                  {section.subsections.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-600">
                      <span className="text-monet-blue">•</span>
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </a>
          ))}
        </div>

        {/* Quick Links */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Quick Links</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quickLinks.map((link, index) => (
              <a
                key={index}
                href={link.link}
                className="flex items-center gap-3 p-4 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <link.icon className="text-monet-blue w-5 h-5" />
                <span className="font-medium text-gray-800">{link.title}</span>
              </a>
            ))}
          </div>
        </div>

        {/* Popular Articles */}
        <div className="mt-16">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Popular Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Preparing 3D Models for Digital Twins
              </h3>
              <p className="text-gray-600 mb-4">
                Learn the essential steps to optimize your 3D models for seamless integration with Monet's platform.
              </p>
              <a href="/docs/preparation" className="text-monet-blue hover:underline">
                Read guide →
              </a>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                API Authentication & Best Practices
              </h3>
              <p className="text-gray-600 mb-4">
                Secure your API connections and implement robust integration patterns with our developer guide.
              </p>
              <a href="/docs/api-authentication" className="text-monet-blue hover:underline">
                Read guide →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DocumentationLanding;